import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Trash2, Pencil, ChevronRight, Droplets, Zap, Loader2 } from "lucide-react";
import { useData } from "../context/DataContext";
import { useSearch, matchesQuery } from "../context/SearchContext";
import { formatKES } from "../utils/format";
import Modal from "../components/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import Tooltip from "../components/Tooltip";

export default function Properties() {
  const { properties, tenants, addProperty, updateProperty, deleteProperty } = useData();
  const { query } = useSearch();
  const navigate = useNavigate();

  const filteredProperties = properties.filter((p) =>
    matchesQuery(query, p.name, p.address)
  );
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", waterEnabled: false, electricityEnabled: false });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const emptyForm = { name: "", address: "", waterEnabled: false, electricityEnabled: false };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setAdding(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      address: p.address || "",
      waterEnabled: !!p.waterEnabled,
      electricityEnabled: !!p.electricityEnabled,
    });
    setAdding(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      if (editing) {
        await updateProperty(editing.id, form);
      } else {
        await addProperty(form);
      }
      setForm(emptyForm);
      setEditing(null);
      setAdding(false);
    } catch (err) {
      console.error("Property save failed:", err);
    }
    setSaving(false);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Properties</h2>
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {filteredProperties.length} {filteredProperties.length === 1 ? "property" : "properties"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage buildings and their tenants. Click a card to view tenants.
          </p>
        </div>
        <button className="btn-primary" onClick={openAdd}>
          <Plus className="h-4 w-4" />
          Add property
        </button>
      </div>

      {properties.length === 0 ? (
        <EmptyState onAdd={openAdd} />
      ) : filteredProperties.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No properties match your search.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProperties.map((p) => {
            const activeTenants = tenants.filter(
              (t) => t.propertyId === p.id && t.status === "ACTIVE"
            );
            const monthly = activeTenants.reduce(
              (a, t) => a + (Number(t.rent) || 0),
              0
            );
            return (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/properties/${p.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/app/properties/${p.id}`);
                  }
                }}
                className="card group cursor-pointer p-5 transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white">
                      {p.name}
                    </h3>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {p.address || "No address"}
                    </p>
                  </div>
                  <Tooltip label="Edit">
                    <button
                      className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(p);
                      }}
                      aria-label="Edit property"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <button
                      className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(p);
                      }}
                      aria-label="Delete property"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Metric label="Active tenants" value={activeTenants.length} />
                  <Metric label="Monthly rent" value={formatKES(monthly)} />
                </div>
                {(p.waterEnabled || p.electricityEnabled) && (
                  <div className="mt-3 flex items-center gap-2">
                    {p.waterEnabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-950 dark:text-blue-400 dark:ring-blue-500/30">
                        <Droplets className="h-3 w-3" /> Water
                      </span>
                    )}
                    {p.electricityEnabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-950 dark:text-amber-400 dark:ring-amber-500/30">
                        <Zap className="h-3 w-3" /> Electricity
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-end text-xs font-medium text-indigo-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  View tenants
                  <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={adding}
        onClose={() => { setAdding(false); setEditing(null); }}
        title={editing ? "Edit property" : "Add property"}
        description={editing ? "Update property details and utility settings." : "Create a new building to track tenants and rent."}
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-base">Property name</label>
            <input
              className="input-base"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label-base">Address</label>
            <input
              className="input-base"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div>
            <label className="label-base">Utility billing (optional)</label>
            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.waterEnabled}
                onChange={(e) => setForm({ ...form, waterEnabled: e.target.checked })}
                className="checkbox-base"
              />
              <Droplets className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Track water billing</span>
            </label>
            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.electricityEnabled}
                onChange={(e) => setForm({ ...form, electricityEnabled: e.target.checked })}
                className="checkbox-base"
              />
              <Zap className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Track electricity billing</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={() => { setAdding(false); setEditing(null); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : "Add property"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.name}?` : ""}
        description="All tenants in this property will be marked as vacated. This cannot be undone."
        confirmLabel="Delete property"
        tone="danger"
        loading={deleting}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await deleteProperty(confirmDelete.id);
          } catch (err) {
            console.error("Delete property failed:", err);
          }
          setDeleting(false);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="card p-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
        <Building2 className="h-6 w-6" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">No properties yet</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Create your first property to start adding tenants and tracking rent.
      </p>
      <button className="btn-primary mt-5" onClick={onAdd}>
        <Plus className="h-4 w-4" />
        Add property
      </button>
    </div>
  );
}
